import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Users, 
  Plus, 
  Calendar as CalendarIcon,
  Settings,
  MoreHorizontal,
  Trash2,
  Edit,
  Eye,
  Loader2,
  CheckCircle,
  Clock,
  Archive,
  Minus,
  CalendarDays
} from 'lucide-react';
import { cn } from '../lib/utils';

const STATUS_CONFIG = {
  active: { color: 'bg-emerald-100 text-emerald-800', label: 'Active', icon: <CheckCircle className="w-3 h-3" /> },
  completed: { color: 'bg-blue-100 text-blue-800', label: 'Completed', icon: <CheckCircle className="w-3 h-3" /> },
  archived: { color: 'bg-gray-100 text-gray-800', label: 'Archived', icon: <Archive className="w-3 h-3" /> }
};

export default function AdminCohorts() {
  const navigate = useNavigate();
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Create form state
  const [newCohort, setNewCohort] = useState({
    name: '',
    description: '',
    capacity: 20,
    startDate: '',
    endDate: ''
  });
  
  useEffect(() => {
    fetchCohorts();
  }, []);
  
  const fetchCohorts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/cohorts');
      setCohorts(response.data.cohorts || []);
    } catch (error) {
      toast.error('Failed to load cohorts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateCohort = async () => {
    if (!newCohort.name.trim()) {
      toast.error('Please enter a cohort name');
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post('/api/admin/cohorts', newCohort);
      toast.success('Cohort created successfully');
      setShowCreateDialog(false);
      setNewCohort({ name: '', description: '', capacity: 20, startDate: '', endDate: '' });
      fetchCohorts();
      
      // Navigate to the new cohort
      navigate(`/admin/cohorts/${response.data.cohort.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create cohort');
    } finally {
      setCreating(false);
    }
  };
  
  const deleteCohort = async (cohortId, cohortName) => {
    if (!window.confirm(`Are you sure you want to delete "${cohortName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`/api/admin/cohorts/${cohortId}`);
      toast.success('Cohort deleted');
      fetchCohorts();
    } catch (error) {
      toast.error('Failed to delete cohort');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div data-testid="cohorts-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl font-semibold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            Cohort Management
          </h1>
          <p className="text-muted-foreground mt-1">Organize and manage participant cohorts</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-cohort-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Cohort
        </Button>
      </div>
      
      <div>
        {cohorts.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-semibold mb-2">No Cohorts Yet</h3>
              <p className="text-muted-foreground mb-6">
                Create your first cohort to start organizing participants
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Cohort
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cohorts.map(cohort => {
              const statusConfig = STATUS_CONFIG[cohort.status] || STATUS_CONFIG.active;
              const memberCount = cohort.memberCount || 0;
              const capacity = cohort.capacity || 20;
              const capacityPercentage = (memberCount / capacity) * 100;
              
              return (
                <Card 
                  key={cohort.id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/admin/cohorts/${cohort.id}`)}
                  data-testid={`cohort-card-${cohort.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{cohort.name}</CardTitle>
                        {cohort.description && (
                          <CardDescription className="mt-1 line-clamp-2">
                            {cohort.description}
                          </CardDescription>
                        )}
                      </div>
                      <Badge className={statusConfig.color}>
                        {statusConfig.icon}
                        <span className="ml-1">{statusConfig.label}</span>
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Capacity */}
                    <div>
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Members</span>
                        <span className="font-medium">{cohort.capacityUsed}</span>
                      </div>
                      <Progress value={capacityPercentage} className="h-2" />
                    </div>
                    
                    {/* Completion Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-muted/50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-600">
                          {cohort.completionStats?.fearAssessment || '0/0'}
                        </p>
                        <p className="text-xs text-muted-foreground">EDGE Complete</p>
                      </div>
                      <div className="text-center p-3 bg-muted/50 rounded-lg">
                        <p className="text-2xl font-bold text-violet-600">
                          {cohort.completionStats?.archetypeAssessment || '0/0'}
                        </p>
                        <p className="text-xs text-muted-foreground">CORE Complete</p>
                      </div>
                    </div>
                    
                    {/* Dates */}
                    {(cohort.startDate || cohort.endDate) && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CalendarIcon className="w-4 h-4" />
                        <span>
                          {cohort.startDate ? formatDate(cohort.startDate) : 'TBD'}
                          {' → '}
                          {cohort.endDate ? formatDate(cohort.endDate) : 'TBD'}
                        </span>
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2 border-t border-border">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/cohorts/${cohort.id}`);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteCohort(cohort.id, cohort.name);
                        }}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Create Cohort Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Cohort</DialogTitle>
            <DialogDescription>
              Create a cohort to group participants together for a program
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Cohort Name *</label>
              <Input
                placeholder="e.g., Pilot Cohort 1"
                value={newCohort.name}
                onChange={(e) => setNewCohort({ ...newCohort, name: e.target.value })}
                data-testid="cohort-name-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Description</label>
              <Textarea
                placeholder="Brief description of this cohort..."
                value={newCohort.description}
                onChange={(e) => setNewCohort({ ...newCohort, description: e.target.value })}
                rows={3}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Capacity</label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setNewCohort({ ...newCohort, capacity: Math.max(1, (newCohort.capacity || 20) - 1) })}
                  disabled={newCohort.capacity <= 1}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={newCohort.capacity}
                  onChange={(e) => setNewCohort({ ...newCohort, capacity: parseInt(e.target.value) || 20 })}
                  className="w-20 text-center"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setNewCohort({ ...newCohort, capacity: Math.min(100, (newCohort.capacity || 20) + 1) })}
                  disabled={newCohort.capacity >= 100}
                >
                  <Plus className="w-4 h-4" />
                </Button>
                <span className="text-sm text-muted-foreground ml-2">participants</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Start Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !newCohort.startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {newCohort.startDate ? format(new Date(newCohort.startDate), "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={newCohort.startDate ? new Date(newCohort.startDate) : undefined}
                      onSelect={(date) => setNewCohort({ ...newCohort, startDate: date ? format(date, 'yyyy-MM-dd') : '' })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">End Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !newCohort.endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {newCohort.endDate ? format(new Date(newCohort.endDate), "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={newCohort.endDate ? new Date(newCohort.endDate) : undefined}
                      onSelect={(date) => setNewCohort({ ...newCohort, endDate: date ? format(date, 'yyyy-MM-dd') : '' })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCohort} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Cohort
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
